import * as React from 'react';

import { enumPurchaseLineItemType } from '@rematter/frontend-lib-api-client';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $addLineItem,
  $itemsInterfaceEntries,
  $itemsInterfaceErrorItemIds,
} from '../_state';
import {
  enumLineItemKind,
  NormalizedDeduction,
  NormalizedFreightCharge,
  NormalizedLineItem,
} from '../_types';
import { ItemRowCatalyticConverter } from '../rows/ItemRowCatalyticConverter';
import { ItemRowDeduction } from '../rows/ItemRowDeduction';
import { ItemRowFreight } from '../rows/ItemRowFreight';
import { ItemRowMaterial } from '../rows/ItemRowMaterial';
import { ItemRowMixed } from '../rows/ItemRowMixed';
import { ItemRowTaggedMaterial } from '../rows/ItemRowTaggedMaterial';
import { ItemRowVehicle } from '../rows/ItemRowVehicle';

import {
  $autoFocusRowId,
  $clearAutoFocusRowId,
  $lastFocusedRowId,
  $setAutoFocusRowId,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type ItemsListProps = {
  newlyAddedItemId: string | null;
};

//
// ⚛️ ⚛️ ⚛️ ITEMS LIST ⚛️ ⚛️ ⚛️
//
export const ItemsList: React.FC<ItemsListProps> = ({ newlyAddedItemId }) => {
  const t = useTranslations();
  const labels = t.scaleTicketPro.itemsInterface;

  // Atoms
  const { items: entries = [] } = useAtomValue($itemsInterfaceEntries);
  const errorItemIds = useAtomValue($itemsInterfaceErrorItemIds);
  const autoFocusRowId = useAtomValue($autoFocusRowId);
  const setLastFocusedRowId = useSetAtom($lastFocusedRowId);
  const clearAutoFocusRowId = useSetAtom($clearAutoFocusRowId);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const addLineItem = useSetAtom($addLineItem);

  // Memoized values
  const topLevelEntries = React.useMemo(
    () =>
      entries.filter((entry) => {
        if (entry.kind === enumLineItemKind.Deduction) {
          const d = entry as NormalizedDeduction;
          return !d.purchaseLoadLineItemId && !d.loadLineItemId;
        }
        if (entry.kind === enumLineItemKind.FreightCharge) return true;
        return !entry.parentLineItemId;
      }),
    [entries],
  );

  const deductionChildrenMap = React.useMemo(() => {
    const map = new Map<string, NormalizedDeduction[]>();
    for (const entry of entries) {
      if (entry.kind !== enumLineItemKind.Deduction) continue;
      const d = entry as NormalizedDeduction;
      const parentId = d.purchaseLoadLineItemId ?? d.loadLineItemId;
      if (!parentId) continue;
      const children = map.get(parentId) ?? [];
      children.push(d);
      map.set(parentId, children);
    }
    return map;
  }, [entries]);

  const childItemsMap = React.useMemo(() => {
    const map = new Map<string, NormalizedLineItem[]>();
    for (const entry of entries) {
      if (entry.kind !== enumLineItemKind.LineItem || !entry.parentLineItemId)
        continue;
      const children = map.get(entry.parentLineItemId) ?? [];
      children.push(entry);
      map.set(entry.parentLineItemId, children);
    }
    return map;
  }, [entries]);

  // Callbacks
  const handleFocusRow = React.useCallback(
    (id: string) => setLastFocusedRowId(id),
    [setLastFocusedRowId],
  );

  const handleAddNextRow = React.useCallback(() => {
    const newId = `temp-${crypto.randomUUID()}`;
    addLineItem(enumPurchaseLineItemType.Raw, { id: newId });
    setAutoFocusRowId(newId);
  }, [addLineItem, setAutoFocusRowId]);

  // Effects
  /** Clear auto-focus row ID after a short delay once it has been consumed */
  React.useEffect(() => {
    if (!autoFocusRowId) return;
    const timer = setTimeout(() => clearAutoFocusRowId(), 500);
    return () => clearTimeout(timer);
  }, [autoFocusRowId, clearAutoFocusRowId]);

  if (entries.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 text-sm text-neutral">
        <span className="text-lg font-semibold">{labels.emptyTitle}</span>
        <span className="text-sm text-neutral/50">
          {labels.emptyDescription}
        </span>
      </div>
    );
  }

  return (
    <>
      {topLevelEntries.map((entry) => {
        if (entry.kind === enumLineItemKind.Deduction) {
          return (
            <ItemRowDeduction
              key={entry.id}
              deduction={entry}
              autoFocus={
                newlyAddedItemId === entry.id || autoFocusRowId === entry.id
              }
              onFocusRow={handleFocusRow}
              hasError={errorItemIds.has(entry.id)}
            />
          );
        }

        if (entry.kind === enumLineItemKind.FreightCharge) {
          return (
            <ItemRowFreight
              key={entry.id}
              freightCharge={entry as NormalizedFreightCharge}
              autoFocus={
                newlyAddedItemId === entry.id || autoFocusRowId === entry.id
              }
              onFocusRow={handleFocusRow}
              hasError={errorItemIds.has(entry.id)}
            />
          );
        }

        const lineItem = entry;
        const baseProps = {
          lineItem,
          autoFocus:
            newlyAddedItemId === lineItem.id || autoFocusRowId === lineItem.id,
          onFocusRow: handleFocusRow,
          hasError: errorItemIds.has(lineItem.id),
        };

        const childDeductions = deductionChildrenMap.get(lineItem.id) ?? [];

        const renderChildDeductions = () =>
          childDeductions.map((deduction) => (
            <ItemRowDeduction
              key={deduction.id}
              deduction={deduction}
              variant="secondary"
              parentLineItem={lineItem}
              siblingDeductions={childDeductions}
              autoFocus={
                newlyAddedItemId === deduction.id ||
                autoFocusRowId === deduction.id
              }
              onFocusRow={handleFocusRow}
              hasError={errorItemIds.has(deduction.id)}
            />
          ));

        switch (lineItem.type) {
          case enumPurchaseLineItemType.Raw:
            return (
              <React.Fragment key={lineItem.id}>
                <ItemRowMaterial
                  {...baseProps}
                  onAddNextRow={handleAddNextRow}
                />
                {renderChildDeductions()}
              </React.Fragment>
            );
          case enumPurchaseLineItemType.Tagged:
            return (
              <React.Fragment key={lineItem.id}>
                <ItemRowTaggedMaterial
                  {...baseProps}
                  onAddNextRow={handleAddNextRow}
                />
                {renderChildDeductions()}
              </React.Fragment>
            );
          case enumPurchaseLineItemType.Vehicle:
            return (
              <React.Fragment key={lineItem.id}>
                <ItemRowVehicle {...baseProps} />
                {renderChildDeductions()}
              </React.Fragment>
            );
          case enumPurchaseLineItemType.CatalyticConverter:
            return (
              <React.Fragment key={lineItem.id}>
                <ItemRowCatalyticConverter {...baseProps} />
                {renderChildDeductions()}
              </React.Fragment>
            );
          case enumPurchaseLineItemType.Mixed:
            return (
              <React.Fragment key={lineItem.id}>
                <ItemRowMixed
                  {...baseProps}
                  childItems={childItemsMap.get(lineItem.id) ?? []}
                  newlyAddedItemId={newlyAddedItemId}
                  errorItemIds={errorItemIds}
                />
                {renderChildDeductions()}
              </React.Fragment>
            );
          default:
            return null;
        }
      })}
    </>
  );
};
